import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import ForcePasswordChange from "./ForcePasswordChange";
import NotificationBell from "./NotificationBell";
import {
  ChartBar,
  ChartLine,
  Users,
  Buildings,
  Funnel,
  SignOut,
  Lightning,
  PlugsConnected,
  PhoneCall,
  ShieldCheck,
  WhatsappLogo,
  MapPin,
  Lock,
  TrendUp,
  Megaphone,
} from "@phosphor-icons/react";

const navItems = [
  { to: "/", label: "Dashboard", icon: ChartBar, end: true, testid: "nav-dashboard" },
  { to: "/leads", label: "Leads", icon: Funnel, testid: "nav-leads" },
  { to: "/sources", label: "Sources", icon: TrendUp, testid: "nav-sources" },
  { to: "/campaigns", label: "Campaigns", icon: Megaphone, testid: "nav-campaigns" },
  { to: "/callyzer", label: "Callyzer", icon: PhoneCall, testid: "nav-callyzer", perm: "can_view_callyzer" },
  { to: "/kraya", label: "Kraya", icon: WhatsappLogo, testid: "nav-kraya", perm: "can_view_kraya" },
  { to: "/reports", label: "Reports", icon: ChartLine, testid: "nav-reports", perm: "can_view_reports" },
  { to: "/branches", label: "Branches", icon: Buildings, testid: "nav-branches", adminOnly: true },
  { to: "/routing", label: "Routing", icon: MapPin, testid: "nav-routing", perm: "can_view_routing", adminOnly: true },
  { to: "/agents", label: "Agents", icon: Users, testid: "nav-agents", adminOnly: true },
  { to: "/permissions", label: "Permissions", icon: Lock, testid: "nav-permissions", adminOnly: true },
  { to: "/security", label: "Security", icon: ShieldCheck, testid: "nav-security", perm: "can_view_security", adminOnly: true },
  { to: "/webhooks", label: "Webhooks", icon: PlugsConnected, testid: "nav-webhooks", perm: "can_view_webhooks", adminOnly: true },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 border-r border-border bg-white flex flex-col" data-testid="app-sidebar">
        <div className="h-16 px-5 flex items-center border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-primary text-primary-foreground flex items-center justify-center">
              <Lightning size={16} weight="fill" />
            </div>
            <div>
              <div className="font-display font-bold text-base leading-none">LeadFlow</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">Ops Console</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 py-4 px-2 space-y-0.5">
          {navItems.filter((i) => {
            // Admin sees every nav. Non-admins filtered by adminOnly or perm flag.
            if (user?.role === "admin") return true;
            if (i.adminOnly) return false;
            if (i.perm && !user?.permissions?.[i.perm]) return false;
            return true;
          }).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors duration-150 border-l-2 ${
                  isActive
                    ? "border-primary bg-primary/5 text-primary font-semibold"
                    : "border-transparent text-foreground/70 hover:text-foreground hover:bg-secondary"
                }`
              }
            >
              <item.icon size={18} weight="regular" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-border p-3">
          <div className="flex items-center gap-2 px-2 py-2">
            <div className="w-8 h-8 bg-secondary flex items-center justify-center text-xs font-bold uppercase">
              {user?.name?.[0] || "U"}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate" data-testid="current-user-name">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{user?.role}</div>
            </div>
            <NotificationBell />
            <button
              onClick={handleLogout}
              className="p-2 hover:bg-secondary transition-colors"
              title="Sign out"
              data-testid="logout-button"
            >
              <SignOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 relative">
        {/* Anti-screenshot watermark — non-admins only */}
        {user && user.role !== "admin" && <ScreenWatermark email={user.email} />}
        <Outlet />
        <ForcePasswordChange />
      </main>
    </div>
  );
}

// Diagonal repeating watermark showing the agent's email + timestamp.
// pointer-events-none + text-[8px] means it never blocks UI but shows
// up in every screenshot/screen-share — creates deterrent against
// "just take a photo of the leads list and send to competitor".
function ScreenWatermark({ email }) {
  const [now] = React.useState(() => new Date().toLocaleString());
  const text = `${email || "user"} · ${now}`;
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-40 overflow-hidden select-none"
      style={{
        opacity: 0.06,
        color: "#000",
        fontFamily: "monospace",
        fontSize: "10px",
        letterSpacing: "0.05em",
        transform: "rotate(-24deg)",
        transformOrigin: "center",
        whiteSpace: "nowrap",
      }}
      data-testid="screen-watermark"
    >
      {Array.from({ length: 60 }).map((_, i) => (
        <div key={i} style={{ padding: "1.5rem 0", textAlign: "center" }}>
          {Array.from({ length: 16 }).map((__, j) => (
            <span key={j} style={{ marginRight: "3rem" }}>{text}</span>
          ))}
        </div>
      ))}
    </div>
  );
}
